import React, { useContext, useState } from "react";
import Button from "../ui/button/Button";
import {
    ComposableMap,
    Geographies,
    Geography,
    ZoomableGroup
} from "react-simple-maps";

import Select from "../ui/select/Select";
import { CountriesSelectOption } from "../../types/countries";
import { useTranslation } from "react-i18next";
import { routesService } from "../../api/routesService/routesService";
import { IRoutesContext, RoutesContext } from "../../context/routesContext";
import { toast } from "react-toastify";

const jsonMap = require('../../assets/data/jsonMap.json');

interface ISidebarProps {
    isOpen?: boolean;
    callbackCloseSidebar: () => void;
    options: CountriesSelectOption[]
}

type CountryTo = {
    value: string;
    type: 'roundtrip' | 'oneWay';
    id: string | null;
}

const SidebarAddRoute: React.FC<ISidebarProps> = ({
    isOpen = false,
    callbackCloseSidebar,
    options
}) => {
    const { t } = useTranslation();

    const [step, setStep] = useState(0);
    const [countryFrom, setCountryFrom] = useState<string>('');
    const [countriesTo, setCountriesTo] = useState<CountryTo[]>([
        {
            type: 'roundtrip',
            value: '',
            id: null
        }
    ]);

    const { routes, setRoutes } = useContext(RoutesContext) as IRoutesContext;

    const [loading, setLoading] = useState(false);

    const isRouteAlreadyUsed = (isoCodeFrom: string, isoCodeTo: string) => {
        return !!routes?.routes.find(r => r.countryFrom.isoCode === isoCodeFrom && r.countryTo.isoCode === isoCodeTo);
    }

    const addRoute = async () => {
        if (!countryFrom || countriesTo[0].value === '' || countriesTo[0].id === null) {
            return;
        }

        let countryFromObj = options.find(c => c.value === countryFrom);

        if (!!countryFromObj) {
            const countryFromId = countryFromObj.id;
            const countryFromIsoCode = countryFromObj.value;

            let routesToAdd: { from: string, to: string }[] = [];
            countriesTo.forEach(cT => {
                if (!!cT.id) {
                    if (isRouteAlreadyUsed(countryFromIsoCode, cT.value) === false) {
                        routesToAdd.push({
                            from: countryFromId,
                            to: cT.id
                        });
                    }

                    if (cT.type === "roundtrip") {
                        if(cT.value !== countryFromIsoCode) {                            
                            if (isRouteAlreadyUsed(cT.value, countryFromIsoCode) === false) {
                                routesToAdd.push({
                                    from: cT.id,
                                    to: countryFromId
                                });
                            }
                        }
                    }
                }
            });

            if (routesToAdd.length > 0) {
                setLoading(true);

                // Request
                const response = await routesService.addRoutes(routesToAdd);

                if (response.success) {
                    // Update routes & close sidebar                    
                    const responseGetRoutes = await routesService.getRoutes();
                    if (responseGetRoutes.success) {
                        setRoutes(responseGetRoutes.responseBody);

                        // Reset
                        setStep(0);
                        setCountryFrom('');
                        setCountriesTo([{
                            type: 'roundtrip',
                            value: '',
                            id: null
                        }]);

                        callbackCloseSidebar();
                    } else {
                        window.location.reload();
                    }
                } else {
                    console.log('error adding routes');
                    toast.error(t('common.errors.addingRoutes'));
                }

                setLoading(false);
            }
        }
    }

    const nextStep = () => {
        if (step === 0) {
            if (countryFrom !== '') {
                setStep(1);
            }
        } else if (step === 1) {
            addRoute();
        }
    }

    const handleChangeCountryFromMap = (e: any) => {
        if (step === 0) {
            if(!!options.find(o => o.value === e.target.getAttribute('id'))) {
                setCountryFrom(e.target.getAttribute('id'));
            }
        } else if (step === 1) {
            if (!getDisabledCountries.some(v => v === e.target.getAttribute('id'))) {
                const copyCountriesTo = [...countriesTo];
                const countryObj = options.find(c => c.value === e.target.getAttribute('id'));
                copyCountriesTo[copyCountriesTo.length - 1].value = !!countryObj ? countryObj.value : '';
                copyCountriesTo[copyCountriesTo.length - 1].id = !!countryObj ? countryObj.id : null;

                setCountriesTo(copyCountriesTo);
            }
        }
    }

    const handleInsertCountryToValue = (value: string, index: number) => {
        const copyCountriesTo = [...countriesTo];
        copyCountriesTo[index].value = value;
        const countryObj = options.find(c => c.value === value);
        copyCountriesTo[index].id = !!countryObj ? countryObj.id : null;
        setCountriesTo(copyCountriesTo)
    }

    const handleInsertCountryToType = (type: ('roundtrip' | 'oneWay'), index: number) => {
        const copyCountriesTo = [...countriesTo];
        copyCountriesTo[index].type = type;
        setCountriesTo(copyCountriesTo)
    }

    const addCountryTo = () => {
        if (countriesTo[countriesTo.length - 1].value !== '') {
            setCountriesTo([
                ...countriesTo,
                {
                    type: 'roundtrip',
                    value: '',
                    id: null
                }
            ])
        }
    }

    const removeCountryTo = (index: number) => () => {
        if (countriesTo.length > 1) {
            const copyCountriesTo = [...countriesTo];
            copyCountriesTo.splice(index, 1);
            setCountriesTo(copyCountriesTo);
        }
    }

    const fillMap = (geoId: string) => {
        let color = '#FFFFFF';
        if (countriesTo.find(c => c.value === geoId) !== undefined) {
            color = '#cf0a2c';
        } else if (geoId === countryFrom) {
            if (step === 0) {
                color = '#cf0a2c';
            } else {
                color = '#FFEAED';
            }
        }

        return color;
    }

    const getDisabledCountries = React.useMemo(() => {
        if (routes !== null) {
            const alreadyUsedRoutes = [...routes.routes].filter((route) => {
                return route.countryFrom.isoCode === countryFrom;
            });

            const alreadyUsedRoutesTo = [...alreadyUsedRoutes].map(r => r.countryTo.isoCode);
            return [...[...countriesTo].map(c => c.value), ...alreadyUsedRoutesTo];
        }

        return [];
    }, [countryFrom, countriesTo]);

    const goToStepOne = () => {
        setCountriesTo([{
            type: 'roundtrip',
            value: '',
            id: null
        }]);
        setStep(0);
    }

    return (
        <aside
            className={"flex flex-col transition z-10 fixed right-0 top-0 h-[100dvh] w-screen md:w-[520px] bg-white overflow-auto hide-scrollbar" + (isOpen === true ? " translate-x-0 sidebar-shadow" : " translate-x-[100%]")}
        >
            <div className="shrink-0 h-[88px] px-[30px] pt-[32px] pb-[24px] border-b flex justify-between items-center">
                <div className="text-lg font-bold leading-[21.6px]">
                    {t('common.sidebars.addRoutes.heading')}
                </div>
                <div onClick={callbackCloseSidebar} className="cursor-pointer">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </div>
            </div>

            {/* MAIN CONTENT OF SIDEBAR */}
            <div className="grow overflow-auto px-[30px] py-8">

                {/* Tabs */}
                <div className="select-none flex mb-6">
                    {/* Step 1 */}
                    <div className="flex items-center w-1/2" onClick={goToStepOne}>
                        <div className={"flex items-center justify-center shrink-0 border h-[44px] w-[44px] rounded-full text-center" + (step === 0 ? ' text-white bg-red border-red' : ' border-[#757575] text-[#757575]')}>
                            <span className="leading-[44px] font-black text-[20px] mb-[1px]">1</span>
                        </div>
                        <div className={"mx-[10px] font-bold shrink-0 text-[15px] uppercase" + (step === 0 ? ' text-red' : ' text-[#757575]')}>{t('common.sidebars.addRoutes.stepOne.heading')}</div>
                        <div className="h-[0.667px] grow bg-[#757575]"></div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex items-center w-1/2">
                        <div className={"flex items-center justify-center shrink-0 border h-[44px] text-[#757575] w-[44px] rounded-full text-center" + (step === 1 ? ' text-white bg-red border-red' : ' border-[#757575] text-[#757575]')}>
                            <span className="leading-[44px] font-black text-[20px] mb-[1px]">2</span>
                        </div>
                        <div className={"mx-[10px] font-bold shrink-0 text-[15px] uppercase" + (step === 1 ? ' text-red' : ' text-[#757575]')}>{t('common.sidebars.addRoutes.stepTwo.heading')}</div>
                        <div className="h-[0.667px] grow bg-[#757575]"></div>
                    </div>
                </div>

                <p className="text-[15px] leading-[18px] mb-6">
                    {
                        step === 0 &&
                        t('common.sidebars.addRoutes.stepOne.paragraph')
                    }
                    {
                        step === 1 &&
                        t('common.sidebars.addRoutes.stepTwo.paragraph')
                    }
                </p>


                <div className="mb-6 md:h-auto -mx-6 md:mx-0 overflow-hidden">
                    {!options || options.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-500 mb-4">
                                Caricamento mappa paesi...
                            </p>
                        </div>
                    ) : (
                        <ComposableMap
                            projection="geoMercator"
                            projectionConfig={{
                                rotate: [-10.0, -53.0, 0],
                                scale: 650
                            }}
                            height={400}
                        >
                            <ZoomableGroup center={[0, 0]} zoom={1}>
                                <Geographies className="cursor-pointer" geography={jsonMap}>
                                    {({ geographies }) =>
                                        geographies.map((geo) => (
                                            <Geography
                                                key={geo.rsmKey}
                                                geography={geo}
                                                fill={fillMap(geo.properties.id)}
                                                stroke={countriesTo.find(c => c.value === geo.properties.id) ? "#FFFFFF" : "#868686"}
                                                strokeWidth={0.3}
                                                id={geo.properties.id}
                                                onClick={handleChangeCountryFromMap}
                                            >
                                                <title>{geo.properties.name}</title>
                                            </Geography>
                                        ))
                                    }
                                </Geographies>
                            </ZoomableGroup>
                        </ComposableMap>
                    )}
                </div>

                <div>
                    {
                        step === 0 && (
                            !options || options.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-500 mb-4">
                                        Caricamento dati paesi...
                                    </p>
                                    <p className="text-sm text-gray-400">
                                        Verificare che i dati dei paesi siano stati caricati correttamente
                                    </p>
                                </div>
                            ) : (
                                <Select
                                    name="country"
                                    options={options}
                                    placeholder={t('common.sidebars.addRoutes.stepOne.selectPlaceholder')}
                                    label={t('common.sidebars.addRoutes.stepOne.selectLabel')}
                                    value={countryFrom}
                                    callbackOnChange={(e) => setCountryFrom(e.target.value)}
                                />
                            )
                        )
                    }
                    {
                        step === 1 && (
                            !options || options.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-500 mb-4">
                                        Caricamento dati paesi...
                                    </p>
                                    <p className="text-sm text-gray-400">
                                        Verificare che i dati dei paesi siano stati caricati correttamente
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <div className="mb-4" onClick={goToStepOne}>
                                        <span className="font-semibold text-[15px] text-red leading-[18px] uppercase">{t('common.sidebars.addRoutes.stepOne.selectLabel')}:</span>
                                        <span className="inline-block ml-1 font-semibold text-[15px]">{options.find(o => o.value === countryFrom)?.label}</span>
                                    </div>

                                    {/* Labels */}
                                    <div className="grid uppercase grid-cols-12 gap-4 mb-[5px] leading-[18px] text-red font-semibold text-[15px]">
                                        <div className="col-span-7">
                                            {t('common.sidebars.addRoutes.stepTwo.selectToLabel')}
                                        </div>
                                        <div className="col-span-5">
                                            {t('common.sidebars.addRoutes.stepTwo.selectTypeLabel')}
                                        </div>
                                    </div>

                                    {/* List selected countries to */}
                                    {
                                        countriesTo.length > 0 &&
                                        <div>
                                            {
                                                countriesTo.map((country, index) => {
                                                    return (
                                                        <div key={index} className="grid grid-cols-12 gap-4 items-center mb-4">
                                                            <div className="col-span-7">
                                                                <Select
                                                                    name="country"
                                                                    options={options}
                                                                    placeholder={t('common.sidebars.addRoutes.stepTwo.selectToPlaceholder')}
                                                                    value={countriesTo[index].value}
                                                                    callbackOnChange={(e) => handleInsertCountryToValue(e.target.value, index)}
                                                                    disabledValues={getDisabledCountries}
                                                                />
                                                            </div>
                                                            <div className="col-span-4">
                                                                <Select
                                                                    name="roundtrip"
                                                                    options={[
                                                                        {
                                                                            label: "Roundtrip",
                                                                            value: "roundtrip"
                                                                        },
                                                                        {
                                                                            label: "One-way",
                                                                            value: "oneWay"
                                                                        }
                                                                    ]}
                                                                    placeholder={t('common.sidebars.addRoutes.stepTwo.selectTypePlaceholder')}
                                                                    value={countriesTo[index].type}
                                                                    callbackOnChange={(e) => handleInsertCountryToType(e.target.value, index)}
                                                                />
                                                            </div>

                                                            {/* Remove */}
                                                            <div onClick={removeCountryTo(index)} className="cursor-pointer col-span-1">
                                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 fill-red">
                                                                    <path fillRule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z" clipRule="evenodd" />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                            }
                                        </div>
                                    }

                                    {/* Add */}
                                    <div onClick={addCountryTo} className=" cursor-pointer bg-red flex items-center justify-center rounded-[8px] h-9 w-9">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="white" className="w-6 h-6">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                        </svg>
                                    </div>


                                </div>
                            )
                        )
                    }
                </div>
            </div>

            <div className={"shrink-0 py-6 px-[30px] flex items-center border-t" + (step === 1 ? " justify-between" : " justify-end")}>
                {
                    step === 1 &&
                    <div className="cursor-pointer uppercase font-bold text-sm flex items-center" onClick={goToStepOne}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 stroke-red">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                        </svg>

                        <div className="ml-2">{t('common.sidebars.addServices.goBack')}</div>
                    </div>
                }

                <Button
                    callback={nextStep}
                    loading={loading}
                >
                    {
                        step === 0 ? t('common.sidebars.addRoutes.stepOne.button') : t('common.sidebars.saveButtonText')
                    }
                </Button>
            </div>
        </aside>
    )
};

export default React.memo(SidebarAddRoute);